import { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { saveNeedsFromNotes } from '../../utils/needsService';
import { useAuth } from '../../contexts/AuthContext';
import { canEditReport } from '../../utils/permissions';
import { AdnReport } from '../../types/adnReport';
import { FormInput } from '../ui/FormInput';
import { MeetingTypeSelect } from '../ui/MeetingTypeSelect';
import { X } from 'lucide-react';
import { toast } from 'sonner';

interface EditAdnReportFormProps {
  report: AdnReport;
  onClose: () => void;
  onUpdate?: () => void;
}

export default function EditAdnReportForm({ report, onClose, onUpdate }: EditAdnReportFormProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<AdnReport>({
    ...report,
    newVisitors: { ...report.newVisitors },
    visitorDecisions: { 
      ...report.visitorDecisions,
      wantsToJoin: { ...report.visitorDecisions.wantsToJoin },
      wantsToGiveLifeToJesus: { ...report.visitorDecisions.wantsToGiveLifeToJesus }
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check permissions on mount
  useEffect(() => {
    if (!canEditReport(report, user)) {
      toast.error('Vous n\'avez pas les permissions pour modifier ce rapport');
      onClose();
    }
  }, [report, user, onClose]);

  const calculateTotals = () => {
    const totalNewVisitors = (formData.newVisitors?.men || 0) + (formData.newVisitors?.women || 0);
    const totalWantsToJoin = (formData.visitorDecisions?.wantsToJoin?.men || 0) + (formData.visitorDecisions?.wantsToJoin?.women || 0);
    const totalWantsToGiveLifeToJesus = (formData.visitorDecisions?.wantsToGiveLifeToJesus?.men || 0) + (formData.visitorDecisions?.wantsToGiveLifeToJesus?.women || 0);
    return { totalNewVisitors, totalWantsToJoin, totalWantsToGiveLifeToJesus };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (!report.id) {
        throw new Error('ID du rapport manquant');
      }

      const { totalNewVisitors, totalWantsToJoin, totalWantsToGiveLifeToJesus } = calculateTotals();
      
      const reportRef = doc(db, 'adnReports', report.id);
      await updateDoc(reportRef, {
        ...formData,
        totalNewVisitors,
        totalWantsToJoin,
        totalWantsToGiveLifeToJesus,
        updatedBy: user?.id,
        updatedAt: new Date()
      });

      // Sauvegarder les besoins si présents et modifiés
      if (formData.needsNotes && formData.needsNotes !== report.needsNotes) {
        await saveNeedsFromNotes({
          reportId: report.id,
          reportType: 'adn',
          department: 'ADN - Amis des Nouveaux',
          needsNotes: formData.needsNotes,
          reportDate: formData.date
        });
      }

      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Error updating ADN report:', error);
      toast.error('Erreur lors de la mise à jour du rapport ADN');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewVisitorsChange = (field: 'men' | 'women', value: string) => {
    const numValue = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      newVisitors: {
        ...prev.newVisitors!,
        [field]: numValue
      }
    }));
  };

  const handleLifeToJesusChange = (field: 'men' | 'women', value: string) => {
    const numValue = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      visitorDecisions: {
        ...prev.visitorDecisions!,
        wantsToGiveLifeToJesus: {
          ...prev.visitorDecisions!.wantsToGiveLifeToJesus,
          [field]: numValue
        }
      }
    }));
  };

  const handleDecisionChange = (type: 'undecided' | 'men' | 'women', value: string) => {
    const numValue = parseInt(value) || 0;
    
    if (type === 'undecided') {
      setFormData(prev => ({
        ...prev,
        visitorDecisions: {
          ...prev.visitorDecisions!,
          undecided: numValue
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        visitorDecisions: {
          ...prev.visitorDecisions!,
          wantsToJoin: {
            ...prev.visitorDecisions!.wantsToJoin,
            [type]: numValue
          }
        }
      }));
    }
  };

  const { totalNewVisitors, totalWantsToJoin, totalWantsToGiveLifeToJesus } = calculateTotals();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Modifier le rapport ADN</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informations générales */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 mb-6">Informations générales</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                label="Date de la rencontre"
                type="date"
                value={formData.date || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                required
              />
              <MeetingTypeSelect
                value={formData.meetingTypeId || ''}
                onChange={(value) => setFormData(prev => ({ ...prev, meetingTypeId: value }))}
                required={true}
              />
            </div>
          </div>

          {/* Nouveaux visiteurs */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 mb-6">Nouveaux visiteurs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                type="number"
                min="0"
                label="Hommes"
                value={formData.newVisitors?.men || 0}
                onChange={(e) => handleNewVisitorsChange('men', e.target.value)}
              />
              <FormInput
                type="number"
                min="0"
                label="Femmes"
                value={formData.newVisitors?.women || 0}
                onChange={(e) => handleNewVisitorsChange('women', e.target.value)}
              />
            </div>
            <div className="mt-4 p-3 bg-[#00665C]/5 rounded-lg">
              <p className="text-[#004d45] font-medium">
                Total des nouveaux visiteurs: {totalNewVisitors}
              </p>
            </div>
          </div>

          {/* Décisions des visiteurs */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 mb-6">Décisions des nouveaux visiteurs</h2>
            <div className="space-y-4">
              <FormInput
                type="number"
                min="0"
                label="Personnes indécises"
                value={formData.visitorDecisions?.undecided || 0}
                onChange={(e) => handleDecisionChange('undecided', e.target.value)}
              />
              
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-700 mb-4">Veulent devenir membres</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    type="number"
                    min="0"
                    label="Hommes"
                    value={formData.visitorDecisions?.wantsToJoin?.men || 0}
                    onChange={(e) => handleDecisionChange('men', e.target.value)}
                  />
                  <FormInput
                    type="number"
                    min="0"
                    label="Femmes"
                    value={formData.visitorDecisions?.wantsToJoin?.women || 0}
                    onChange={(e) => handleDecisionChange('women', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-700 mb-4">Veulent donner leur vie à Jésus</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    type="number"
                    min="0"
                    label="Hommes"
                    value={formData.visitorDecisions?.wantsToGiveLifeToJesus?.men || 0}
                    onChange={(e) => handleLifeToJesusChange('men', e.target.value)}
                  />
                  <FormInput
                    type="number"
                    min="0"
                    label="Femmes"
                    value={formData.visitorDecisions?.wantsToGiveLifeToJesus?.women || 0}
                    onChange={(e) => handleLifeToJesusChange('women', e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-green-800 font-medium">
                  Total veulent devenir membres: {totalWantsToJoin}
                </p>
              </div>
              <div className="p-3 bg-[#00665C]/8 rounded-lg">
                <p className="text-[#004d45] font-medium">
                  Total veulent donner leur vie à Jésus: {totalWantsToGiveLifeToJesus}
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Notes / Observations
            </label>
            <textarea
              placeholder="Observations particulières..."
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
              value={formData.notes || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            />
          </div>

          {/* Besoins */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Liste des besoins
            </label>
            <textarea
              placeholder="Listez les besoins du département (un besoin par ligne)..."
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
              value={formData.needsNotes || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, needsNotes: e.target.value }))}
            />
            <p className="mt-1 text-xs text-gray-500">
              Entrez un besoin par ligne. Ces besoins seront ajoutés à la liste de suivi des besoins du département.
            </p>
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Mise à jour...' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
